<template>
  <component :is="quotationData === undefined ? 'div' : 'b-card'">
    <b-alert variant="danger" :show="quotationData === undefined">
      <h4 class="alert-heading">獲取報價單數據時出錯</h4>
      <div class="alert-body">
        找不到相關報價單記錄。請查看
        <b-link class="alert-link" :to="{ name: 'quotations-list' }">
          報價單列表
        </b-link>
      </div>
    </b-alert>
    <quotation-edit-tab-information
      v-if="quotationData"
      :quotation-type-options="quotationTypeOptions"
      :customer-options="customerOptions"
      :project-options="projectOptions"
      :product-options="productOptions"
      :quotation-data="quotationData"
      :product-categories-options="productCategoriesOptions"
      :product-unit-options="productUnitOptions"
      :pdf-remark-options="pdfRemarkOptions"
      :quotation-description-options="quotationDescriptionOptions"
      :template-options="templateOptions"
      :stamp-options="stampOptions"
      @refetch-data="refetchData"
      @fetch-options="refetchOption"
      class="p-sm-2"
    />

  </component>
</template>

<script>
import { BCard, BAlert, BLink, BTabs, BTab } from "bootstrap-vue";
import { ref, onUnmounted, onMounted, nextTick } from "@vue/composition-api";
import store from "@/store";
import router from "@/router";
import quotationStoreModule from "../quotationStoreModule";
import QuotationEditTabInformation from "./quotationEditTabInformation.vue";

export default {
  components: {
    QuotationEditTabInformation,
    BCard,
    BAlert,
    BTabs,
    BTab,
    BLink,
  },
  setup() {
    const quotationData = ref({ id: 0, productOptions: [], remark_id: 1, quotation_description_id: null });
    const quotation = {
      id: 0,
      stamp_id: null,
      template_id: 1,
      //salary_items: [{ id: 0 }],
      cost_items: [{ id: 0, productOptions: [], size: '', empty_rows: 0 }],
      quotation_date: new Date().toISOString().substr(0, 10),
      quotation_type_id: 1,
      amount: 0,
      coupon_id:null,
      gift_voucher_id:null,
      remark_id: 1,
      quotation_description_id: null,
      quotation_remark: null,
      remark: null,
    };

    const QUOTATION_APP_STORE_MODULE_NAME = "quotation";

    if (!store.hasModule(QUOTATION_APP_STORE_MODULE_NAME))
      store.registerModule(
        QUOTATION_APP_STORE_MODULE_NAME,
        quotationStoreModule
      );

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(QUOTATION_APP_STORE_MODULE_NAME))
        store.unregisterModule(QUOTATION_APP_STORE_MODULE_NAME);
    });

    const refetchData = () => {
      if (router.currentRoute.name == "quotation-create") {
        quotationData.value = quotation;
        
        // 處理來自客戶列表的客戶ID query參數
        if (router.currentRoute.query.customer_id) {
          quotationData.value.customer_id = parseInt(router.currentRoute.query.customer_id);
        }
        
        // quotationData.value.remark_id = 1;
        // quotationData.value.quotation_description_id = 1;
      } else if (router.currentRoute.name == "quotation-copy") {
        // 複製模式：載入原報價單資料但重設ID為0以建立新報價單
        store
          .dispatch("quotation/fetchQuotation", { id: router.currentRoute.params.id })
          .then((response) => {
            const originalQuotation = response.data.quotation;
            quotationData.value = {...quotation, ...originalQuotation};

            // 重設ID為0以確保建立新報價單
            quotationData.value.id = 0;

            // 清除報價單編號，讓系統自動生成新編號
            quotationData.value.quotation_number = '';

            // 重設日期為今天
            quotationData.value.quotation_date = new Date().toISOString().substr(0, 10);

            // 重設金額欄位
            quotationData.value.amount = 0;

            // 清除優惠券和禮券記錄（這些不應該被複製）
            quotationData.value.coupon_record_id = null;
            quotationData.value.coupon_discount = 0;
            quotationData.value.gift_voucher_record_id = null;
            quotationData.value.gift_voucher_discount = 0;

            // 處理cost_items的ID和productOptions
            quotationData.value.cost_items.forEach(cost => {
              cost.id = 0; // 重設cost item ID
              cost.quantity = parseFloat(Number(cost.quantity).toString());
              // 初始化 productOptions 為空陣列，稍後會在子組件中正確載入
              cost.productOptions = [];
            });

            // 使用 nextTick 確保在 productOptions 載入後處理
            nextTick(() => {
              if (productOptions.value && productOptions.value.length > 0) {
                quotationData.value.cost_items.forEach(cost => {
                  if (cost.product_category_id) {
                    // 為每個項目設定對應的 productOptions
                    cost.productOptions = productOptions.value.filter(
                      product => product.product_category_id == cost.product_category_id
                    );
                    console.log(`報價單複製：為項目 ${cost.item_description} 載入了 ${cost.productOptions.length} 個產品選項`);
                  }
                });
              }
            });
          })
          .catch((error) => {
            console.log("error when fetching quotation for copy", error);
            if (error.response.status === 404) {
              quotationData.value = undefined;
            }
          });
      } else {
        // 編輯模式：載入現有報價單資料
        store
          .dispatch("quotation/fetchQuotation", { id: router.currentRoute.params.id })
          .then((response) => {
            quotationData.value = {...quotation, ...response.data.quotation};
            quotationData.value.cost_items.forEach(cost => {
              cost.quantity = parseFloat(Number(cost.quantity).toString());
            });
          })
          .catch((error) => {
            console.log("error when fetching quotation", error);
            if (error.response.status === 404) {
              quotationData.value = undefined;
            }
          });
      }
    };

    const quotationTypeOptions = ref([]);
    const customerOptions = ref([]);
    const projectOptions = ref([]);
    const productCategoriesOptions = ref([]);
    const productOptions = ref([]);
    const productUnitOptions = ref([]);
    const pdfRemarkOptions = ref([]);
    const quotationDescriptionOptions = ref([]);
    const templateOptions = ref([]);
    const stampOptions = ref([]);

    const refetchOption = () => {
      store
        .dispatch("app/fetchOptionList", {
          quotation_types: true,
          projects: true,
          customers: true,
          products: true,
          product_categories: true,
          product_units: true,
          quotation_remarks: true,
          quotation_descriptions: true,
          templates: true,
          stamps: true,
        })
        .then((response) => {
          quotationTypeOptions.value = response.data.quotation_types;
          projectOptions.value = response.data.projects;
          customerOptions.value = response.data.customers;
          productOptions.value = response.data.products;          
          productCategoriesOptions.value = response.data.product_categories;
          let product_units = response.data.product_units;
          productUnitOptions.value = product_units;
          pdfRemarkOptions.value = response.data.quotation_remarks; 
          quotationDescriptionOptions.value = response.data.quotation_descriptions;
          templateOptions.value = response.data.templates;
          stampOptions.value = response.data.stamps;
          refetchData();
        });
        
    };

    onMounted(() => {
      refetchOption();
    });


    return {
      quotationTypeOptions,
      customerOptions,
      projectOptions,
      productOptions, 
      productCategoriesOptions,
      quotationData,
      productUnitOptions,
      quotationDescriptionOptions,
      pdfRemarkOptions,
      refetchData,
      refetchOption,  
      templateOptions,
      stampOptions,
    };
  },
};
</script>

